import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Container, Row, Col, Alert, Spinner } from "react-bootstrap";
import { Styles } from "./styles/courseFilter.js";
import { COURSES_ENDPOINT } from "../config/api";

const SECTION_TITLE =
  "We Do What We Love To Do. Find the Best Course To Fit Your Needs.";

const formatCurrency = (amount, currency) => {
  if (typeof amount !== "number" || Number.isNaN(amount) || amount <= 0) {
    return "Tuition details on request";
  }

  const formattedAmount = new Intl.NumberFormat("en-US", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);

  return `${currency || ""} ${formattedAmount}`.trim();
};

const resolveTuitionAmount = (course = {}) => {
  if (
    typeof course.tuitionFeePerYear === "number" &&
    course.tuitionFeePerYear > 0
  ) {
    return course.tuitionFeePerYear;
  }

  if (
    typeof course.tuitionFeePerSemester === "number" &&
    course.tuitionFeePerSemester > 0
  ) {
    return course.tuitionFeePerSemester * 2;
  }

  if (
    typeof course.tuitionFeePerCourse === "number" &&
    course.tuitionFeePerCourse > 0
  ) {
    return course.tuitionFeePerCourse;
  }

  return null;
};

const buildCourseDescription = (course = {}) => {
  if (course.concentration) {
    return course.concentration;
  }

  if (course.levelOfStudy && course.location) {
    return `${course.levelOfStudy} program located in ${course.location}.`;
  }

  if (course.studyMode || course.mode) {
    return `Study mode: ${course.studyMode || course.mode}.`;
  }

  return `Study ${course.programName || "this program"} at ${
    course.universityName || "Swiss School"
  }.`;
};

const formatDuration = (course = {}) => {
  if (!course.duration) {
    return "Duration TBD";
  }

  return course.duration;
};

const CourseFilter = () => {
  const [coursesByCategory, setCoursesByCategory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("All Courses");

  useEffect(() => {
    const controller = new AbortController();

    const loadCourses = async () => {
      setLoading(true);
      setError("");
      try {
        const response = await fetch(`${COURSES_ENDPOINT}?limit=200`, {
          signal: controller.signal,
        });
        if (!response.ok) {
          const payload = await response.json().catch(() => ({}));
          throw new Error(payload.message || "Unable to load courses.");
        }

        const payload = await response.json();
        const courses = Array.isArray(payload.courses) ? payload.courses : [];

        const grouped = new Map();
        courses.forEach((course) => {
          const category = course.levelOfStudy?.trim() || "Other";
          if (!grouped.has(category)) {
            grouped.set(category, []);
          }
          const bucket = grouped.get(category);
          if (bucket.length < 2) {
            bucket.push(course);
          }
        });

        const groupedArray = Array.from(grouped.entries())
          .filter(([, list]) => list.length > 0)
          .map(([label, list]) => ({ label, courses: list }))
          .sort((a, b) => a.label.localeCompare(b.label));

        setCoursesByCategory(groupedArray);
      } catch (err) {
        if (err.name !== "AbortError") {
          setError(err.message || "Unable to load courses.");
          setCoursesByCategory([]);
        }
      } finally {
        if (!controller.signal.aborted) {
          setLoading(false);
        }
      }
    };

    loadCourses();

    return () => controller.abort();
  }, []);

  const flatCourses = useMemo(
    () => coursesByCategory.flatMap((category) => category.courses),
    [coursesByCategory]
  );

  const allCourses = useMemo(
    () => coursesByCategory.flatMap((category) => category.courses),
    [coursesByCategory]
  );

  const displayedCourses =
    selectedCategory === "All Courses"
      ? allCourses
      : coursesByCategory.find((c) => c.label === selectedCategory)?.courses ||
        [];

  const renderCourseCard = (course) => {
    const courseLink = process.env.PUBLIC_URL + `/courses/${course._id || ""}`;
    const tuitionAmount = resolveTuitionAmount(course);
    const tuitionLabel = formatCurrency(tuitionAmount, course.currency);
    const description = buildCourseDescription(course);
    const levelLabel = course.levelOfStudy || "Program";
    const locationLabel = course.location || "Multiple locations";
    const durationLabel = formatDuration(course);

    return (
      <div className="course-item">
        <div className="course-content">
          <h6 className="heading">
            <Link to={courseLink}>{course.programName || "Course"}</Link>
          </h6>
          <div className="course-price mb-2">
            <p>{tuitionLabel}</p>
          </div>
          <p className="desc">{course.universityName}</p>
          <div className="course-face d-flex justify-content-between">
            <div className="duration">
              <p>
                <i className="las la-user-graduate"></i>
                {levelLabel}
              </p>
            </div>
            <div className="student">
              <p>
                <i className="las la-map-marker-alt"></i>
                {locationLabel}
              </p>
            </div>
            <div className="duration">
              <p>
                <i className="las la-clock"></i>
                {durationLabel}
              </p>
            </div>
          </div>
          <div className="course-actions d-flex justify-content-between mt-3">
            <Link to={courseLink} className="btn btn-outline-secondary btn-sm">
              Apply Now
            </Link>
            <Link to={courseLink} className="btn btn-link btn-sm">
              View details
            </Link>
          </div>
        </div>
      </div>
    );
  };

  return (
    <Styles>
      <section className="course-filter">
        <Container>
          <Row>
            <Col md="12">
              <div className="sec-title text-center">
                <h4>{SECTION_TITLE}</h4>
              </div>
            </Col>
          </Row>

          {loading && (
            <div className="text-center my-5">
              <Spinner animation="border" role="status" />
            </div>
          )}

          {!loading && error && (
            <Alert variant="danger" className="text-center">
              {error}
            </Alert>
          )}

          {!loading && !error && coursesByCategory.length === 0 && (
            <Alert variant="info" className="text-center">
              No courses available right now.
            </Alert>
          )}

          <>
            <div className="category-heading text-center mb-4">
              <button
                onClick={() => setSelectedCategory("All Courses")}
                className={`category-btn ${
                  selectedCategory === "All Courses" ? "active" : ""
                }`}
              >
                All Courses
              </button>
              {coursesByCategory.map((category) => (
                <button
                  key={category.label}
                  onClick={() => setSelectedCategory(category.label)}
                  className={`category-btn ${
                    selectedCategory === category.label ? "active" : ""
                  }`}
                >
                  {category.label}
                </button>
              ))}
            </div>

            {!loading && !error && coursesByCategory.length > 0 && (
              <div className="categories-wrapper">
                {coursesByCategory
                  .filter(
                    (cat) =>
                      selectedCategory === "All Courses" ||
                      cat.label === selectedCategory
                  )
                  .map((category) => (
                    <div className="category-block" key={category.label}>
                      <Row>
                        {category.courses.map((course, index) => (
                          <Col
                            lg="6"
                            md="12"
                            key={course._id || `${category.label}-${index}`}
                          >
                            {renderCourseCard(course)}
                          </Col>
                        ))}
                      </Row>
                    </div>
                  ))}
              </div>
            )}
          </>

          {!loading && !error && flatCourses.length > 0 && (
            <Row>
              <Col md="12" className="text-center">
                <div className="viewall-btn">
                  <Link to={process.env.PUBLIC_URL + "/courses"}>
                    View All Courses
                  </Link>
                </div>
              </Col>
            </Row>
          )}
        </Container>
      </section>
    </Styles>
  );
};

export default CourseFilter;
